import { buildAuthHeaders } from '@frontend-platform/auth-session';
import {
  getRuntimeConfig,
  type PlatformConfig,
  subscribeToRuntimeConfig,
} from '@frontend-platform/config';

type QueryPrimitive = string | number | boolean | undefined | null;
type QueryValue =
  | QueryPrimitive
  | QueryPrimitive[]
  | Record<string, QueryPrimitive | QueryPrimitive[]>;

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface ApiClientOptions {
  baseUrl?: string;
  defaultHeaders?: Record<string, string>;
  includeAuth?: boolean;
  fetchFn?: typeof fetch;
  onError?: (error: ApiError) => void;
}

export interface ApiRequestOptions<TBody = unknown> {
  path: string;
  method?: HttpMethod;
  body?: TBody;
  query?: Record<string, QueryValue>;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  credentials?: RequestCredentials;
  cache?: RequestCache;
  mode?: RequestMode;
  redirect?: RequestRedirect;
  referrer?: string;
  referrerPolicy?: ReferrerPolicy;
  integrity?: string;
  keepalive?: boolean;
  includeAuth?: boolean;
  responseType?: 'auto' | 'json' | 'text' | 'blob' | 'arrayBuffer' | 'response';
}

function readBackendErrorMessage(data: unknown): string | null {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const { message, statusMessage } = data as Record<string, unknown>;
  for (const value of [statusMessage, message]) {
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }

  return null;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly response: Response,
    public readonly data: unknown,
    public readonly url: string
  ) {
    super(readBackendErrorMessage(data) ?? message);
    this.name = 'ApiError';
  }
}

export function getApiErrorMessage(error: unknown, fallback = 'Something went wrong'): string {
  const apiErrorData =
    error instanceof ApiError
      ? error.data
      : (error as { response?: { data?: unknown } } | null)?.response?.data;
  const backendMessage = readBackendErrorMessage(apiErrorData);
  if (backendMessage) {
    return backendMessage;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  if (typeof error === 'string' && error.trim()) {
    return error;
  }

  return fallback;
}

export class ApiClient {
  private baseUrl!: string;
  private readonly includeAuth: boolean;
  private readonly fetchFn: typeof fetch;
  private readonly defaultHeaders: Record<string, string>;
  private readonly onError?: (error: ApiError) => void;

  private unsubscribeFromConfig?: () => void;

  constructor(options: ApiClientOptions = {}) {
    this.includeAuth = options.includeAuth ?? true;
    this.fetchFn = options.fetchFn ?? fetch.bind(globalThis);
    this.defaultHeaders = { ...(options.defaultHeaders ?? {}) };
    this.onError = options.onError;
    const applyRuntimeConfig = (config: PlatformConfig) => {
      if (!options.baseUrl) {
        this.baseUrl = config.apiBaseUrl;
      }
      if (!options.defaultHeaders?.['X-Frontend-Environment']) {
        this.defaultHeaders['X-Frontend-Environment'] = config.environment;
      }
    };
    applyRuntimeConfig(getRuntimeConfig());
    if (!options.baseUrl || !options.defaultHeaders?.['X-Frontend-Environment']) {
      this.unsubscribeFromConfig = subscribeToRuntimeConfig((config) => {
        applyRuntimeConfig(config);
      });
    }
    if (options.baseUrl) {
      this.baseUrl = options.baseUrl;
    }
  }

  async request<TResponse = unknown, TBody = unknown>({
    method = 'GET',
    path,
    body,
    query,
    headers,
    signal,
    credentials,
    cache,
    mode,
    redirect,
    referrer,
    referrerPolicy,
    integrity,
    keepalive,
    includeAuth,
    responseType = 'auto',
  }: ApiRequestOptions<TBody>): Promise<TResponse> {
    const url = this.buildUrl(path, query);
    const mergedHeaders: Record<string, string> = {
      ...this.defaultHeaders,
      ...(headers ?? {}),
    };

    const shouldIncludeAuth = includeAuth ?? this.includeAuth;
    if (shouldIncludeAuth) {
      Object.assign(mergedHeaders, buildAuthHeaders());
    }

    const hasBody = body !== undefined && method !== 'GET';
    const isFormData = hasBody && typeof FormData !== 'undefined' && body instanceof FormData;
    const hasExplicitContentType =
      'Content-Type' in mergedHeaders || 'content-type' in mergedHeaders;

    if (hasBody) {
      if (isFormData) {
        delete mergedHeaders['Content-Type'];
        delete mergedHeaders['content-type'];
      } else if (!hasExplicitContentType) {
        mergedHeaders['Content-Type'] = 'application/json';
      }
    }

    const options: RequestInit = {
      method,
      headers: mergedHeaders,
      signal,
      credentials,
      cache,
      mode,
      redirect,
      referrer,
      referrerPolicy,
      integrity,
      keepalive,
    };

    if (hasBody) {
      options.body =
        mergedHeaders['Content-Type'] === 'application/json'
          ? JSON.stringify(body)
          : (body as BodyInit);
    }

    const response = await this.fetchFn(url, options);
    let data: unknown;

    try {
      data = await this.parseResponse(response, responseType, url);
    } catch (error) {
      const parseError =
        error instanceof ApiError
          ? error
          : new ApiError(`Failed to parse response from ${url}`, response, undefined, url);
      this.onError?.(parseError);
      throw parseError;
    }

    if (!response.ok) {
      const error = new ApiError(`Request to ${url} failed`, response, data, url);
      this.onError?.(error);
      throw error;
    }

    return data as TResponse;
  }

  get<T = unknown>(
    path: string,
    options: Omit<ApiRequestOptions, 'path' | 'method' | 'body'> = {}
  ) {
    return this.request<T>({ ...options, path, method: 'GET' });
  }

  post<T = unknown, TBody = unknown>(
    path: string,
    body?: TBody,
    options: Omit<ApiRequestOptions<TBody>, 'path' | 'method' | 'body'> = {}
  ) {
    return this.request<T, TBody>({ ...options, path, body, method: 'POST' });
  }

  put<T = unknown, TBody = unknown>(
    path: string,
    body?: TBody,
    options: Omit<ApiRequestOptions<TBody>, 'path' | 'method' | 'body'> = {}
  ) {
    return this.request<T, TBody>({ ...options, path, body, method: 'PUT' });
  }

  patch<T = unknown, TBody = unknown>(
    path: string,
    body?: TBody,
    options: Omit<ApiRequestOptions<TBody>, 'path' | 'method' | 'body'> = {}
  ) {
    return this.request<T, TBody>({ ...options, path, body, method: 'PATCH' });
  }

  delete<T = unknown>(
    path: string,
    options: Omit<ApiRequestOptions, 'path' | 'method' | 'body'> = {}
  ) {
    return this.request<T>({ ...options, path, method: 'DELETE' });
  }

  /**
   * Cleanup method to unsubscribe from runtime config changes.
   * Should be called when the ApiClient instance is no longer needed.
   */
  destroy(): void {
    if (this.unsubscribeFromConfig) {
      this.unsubscribeFromConfig();
      this.unsubscribeFromConfig = undefined;
    }
  }

  private buildUrl(path: string, query?: ApiRequestOptions['query']): string {
    const base = this.baseUrl.replace(/\/$/, '');
    const resolvedPath = path.startsWith('http')
      ? path
      : path
        ? `${base}/${path.replace(/^\//, '')}`
        : base;

    const hasAbsoluteBase = resolvedPath.startsWith('http');
    const url = hasAbsoluteBase ? new URL(resolvedPath) : new URL(resolvedPath, 'http://localhost');

    if (!query) {
      return hasAbsoluteBase ? url.toString() : `${url.pathname}${url.search}`;
    }

    Object.entries(query).forEach(([key, value]) => {
      this.appendQueryParam(url.searchParams, key, value);
    });

    const serialized = url.toString();
    return hasAbsoluteBase ? serialized : `${url.pathname}${url.search}`;
  }

  private appendQueryParam(params: URLSearchParams, key: string, value: QueryValue): void {
    if (value === undefined || value === null) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((entry) => {
        this.appendQueryParam(params, key, entry as QueryValue);
      });
      return;
    }

    if (typeof value === 'object') {
      Object.entries(value).forEach(([childKey, childValue]) => {
        this.appendQueryParam(params, `${key}[${childKey}]`, childValue as QueryValue);
      });
      return;
    }

    params.append(key, String(value));
  }

  private async parseResponse(
    response: Response,
    responseType: NonNullable<ApiRequestOptions['responseType']>,
    url: string
  ): Promise<unknown> {
    if (responseType === 'response') {
      return response;
    }

    if (!this.responseHasBody(response)) {
      return null;
    }

    const contentType = response.headers.get('content-type') ?? '';
    let targetType: NonNullable<ApiRequestOptions['responseType']>;
    if (responseType === 'auto') {
      targetType = contentType.includes('application/json') ? 'json' : 'text';
    } else {
      targetType = responseType;
    }

    if (targetType === 'json') {
      const text = await response.text();
      if (!text) {
        return null;
      }

      try {
        return JSON.parse(text);
      } catch {
        throw new ApiError(`Failed to parse JSON response from ${url}`, response, text, url);
      }
    }

    if (targetType === 'blob') {
      return response.blob();
    }

    if (targetType === 'arrayBuffer') {
      return response.arrayBuffer();
    }

    return response.text();
  }

  private responseHasBody(response: Response): boolean {
    if (response.status === 204 || response.status === 205 || response.status === 304) {
      return false;
    }

    const contentLength = response.headers.get('content-length');
    if (contentLength === '0') {
      return false;
    }

    return response.body !== null;
  }
}

export const sharedApiClient = new ApiClient();

export function createApiClient(options?: ApiClientOptions) {
  return new ApiClient(options);
}
